import { formatMoney, type PlanRevenue } from '@features/analytics/model/dashboard.types';
import { chartColors } from '@shared/lib/chart-theme';

/**
 * MRR per plan, as a labelled bar list rather than a chart component.
 *
 * Every row carries its own number, which is what makes revenue concentration
 * readable at a glance — the thing an investor checks first. Plans that bring in
 * no recurring revenue still appear, with their subscriber count, so a large
 * free tier is not invisible.
 */
export function PlanRevenueChart({ data, currency }: { data: PlanRevenue[]; currency: string }) {
  if (data.length === 0) {
    return <p className="py-10 text-center text-sm text-slate-400">No active plans.</p>;
  }

  const max = Math.max(...data.map((plan) => plan.mrr), 1);

  return (
    <ul className="space-y-3">
      {data.map((plan) => {
        const share = (plan.mrr / max) * 100;

        return (
          <li key={plan.label}>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate text-slate-700">{plan.label}</span>
              <span className="shrink-0 tabular-nums">
                <span className="font-semibold text-slate-900">
                  {formatMoney(plan.mrr, currency)}
                </span>
                <span className="ml-1.5 text-xs text-slate-400">
                  {plan.subscribers} {plan.subscribers === 1 ? 'sub' : 'subs'}
                </span>
              </span>
            </div>

            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.max(share, plan.mrr > 0 ? 2 : 0)}%`,
                  backgroundColor: chartColors.primary,
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
